import React from "react";

import { trDts } from "@modules/datasheets/utils/i18n";
import { MessageBox } from "@common/components/MessageBox";
import { Flex } from "@common/components/Flex";
import { FormControl } from "@common/components/form-control";
import { getTestAttributes } from "@common/utils/tests";
import { useMessage } from "@common/hooks/message";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@common/components/modal";
import { Button } from "@common/components/Button";
import { HttpErrorDto } from "@common/dto/http";
import { DatasheetSettingsConfigureTableFormat } from "../../configure-table.types";

import "./format-name.styles.scss";

export type DatasheetConfigureTableFormatNamProps = {
  onSubmit: (formatName: string) => void;
  onClose: () => void;
  checkFormatNameExists: (formatName: string) => Promise<boolean>;
  formats: Array<DatasheetSettingsConfigureTableFormat>;
};

export const FormatName: React.FC<DatasheetConfigureTableFormatNamProps> = (props) => {
  const { onClose, onSubmit, checkFormatNameExists, formats } = props;

  const { closeMessage, showErrorMessage, messageProps } = useMessage();

  const [formatName, setFormatName] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);
    try {
      const isExists = await checkFormatNameExists(formatName);
      if (isExists) {
        showErrorMessage(trDts("The format name already exists"));
        setSubmitting(false);
        return;
      }

      try {
        await onSubmit(formatName.trim());
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to save format"));
      } finally {
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || trDts("Failed to check format name exists"));
      setSubmitting(false);
    }
  }, [showErrorMessage, formatName, onSubmit, checkFormatNameExists]);

  const submittingDisabled = !formatName.trim().length || submitting;

  return (
    <Modal
      show
      backdrop="static"
      bsSize="md"
      bsClass="bootstrap modal"
      className="DatasheetElementSettingsConfigureTableFormatName"
      dialogClassName="DatasheetElementSettingsConfigureTableFormatName__dialog"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes({ id: "fp-table-format-save-as-wnd-1" })}>
          {trDts("Save Table Format")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
        <Flex className="DatasheetElementSettingsConfigureTableFormatName__content__format-name" alignItems="center">
          <span className="DatasheetElementSettingsConfigureTableFormatName__content__format-name__label">
            {trDts("Format Name")}
          </span>
          <FormControl
            className="DatasheetElementSettingsConfigureTableFormatName__content__format-name__input"
            {...getTestAttributes({ id: "txt-table-format-save-as-wnd-format-name" })}
            value={formatName}
            onChange={(e) => setFormatName(e.target.value)}
          />
        </Flex>
        <div className="DatasheetElementSettingsConfigureTableFormatName__content__formats">
          {formats.map((format, formatIndex) => (
            <Flex
              key={format.id}
              alignItems="center"
              {...getTestAttributes({ id: `bsgrid-${formatIndex}-0` })}
              onClick={() => setFormatName(format.name)}
              className="DatasheetElementSettingsConfigureTableFormatName__content__formats__item"
            >
              <img src="gif/format.gif" />
              <span>{format.name}</span>
            </Flex>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button {...getTestAttributes({ id: "btn-table-format-save-as-wnd-cancel" })} onClick={onClose}>
            {trDts("Cancel")}
          </Button>
          <Button
            {...getTestAttributes({ id: "btn-table-format-save-as-wnd-save" })}
            disabled={submittingDisabled}
            bsStyle="primary"
            onClick={handleSubmit}
          >
            {submitting ? trDts("Loading") : trDts("Save")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
